import type { Abrechnung as AbrechnungDaten } from '@nac2s/shared'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Select } from '@/components/base/select/select'
import { Knopf } from '@/teile/Knopf'
import { Status } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import { abrechnungCsvAdresse, ladeAbrechnung, ladeEditionen } from '../api/verwaltung.js'
import { Seitenkopf } from '../layout/AppShell.js'
import { aktuellerMonat, datum, euro, istMonat, monatsauswahl, monatsname } from '../teile/monat.js'

/**
 * Abrechnung je Monat: Mengen × Preise je Partner, zur Übergabe an das
 * Rechnungssystem (Autotask, weclapp). Die Rechnung selbst entsteht dort;
 * hier steht, was hinein muss — als Tabelle und als CSV.
 */

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; abrechnungen: AbrechnungDaten[] }
  | { status: 'fehler'; meldung: string }

export function Abrechnung() {
  const [params, setParams] = useSearchParams()
  const monatParam = params.get('monat')
  const monat = monatParam && istMonat(monatParam) ? monatParam : aktuellerMonat()
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [editionsnamen, setEditionsnamen] = useState<ReadonlyMap<string, string>>(new Map())

  useEffect(() => {
    let aktuell = true
    ladeEditionen()
      .then((e) => {
        if (aktuell) setEditionsnamen(new Map(e.map((x) => [x.key, x.name])))
      })
      .catch(() => {
        // Ohne Namen stehen die Schluessel — lesbar genug.
      })
    return () => {
      aktuell = false
    }
  }, [])

  useEffect(() => {
    let aktuell = true
    setZustand({ status: 'laedt' })
    ladeAbrechnung(monat)
      .then((abrechnungen) => {
        if (aktuell) setZustand({ status: 'da', abrechnungen })
      })
      .catch((fehler: unknown) => {
        if (aktuell)
          setZustand({
            status: 'fehler',
            meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
          })
      })
    return () => {
      aktuell = false
    }
  }, [monat])

  const summe =
    zustand.status === 'da' ? zustand.abrechnungen.reduce((s, a) => s + a.gesamtCent, 0) : 0

  return (
    <div>
      <Seitenkopf
        beisatz={monatsname(monat)}
        titel="Abrechnung"
        satz="Peak je Monat, Demo zählt nicht — je Partner die Positionen für das Rechnungssystem."
        rechts={
          <Knopf stark onClick={() => window.location.assign(abrechnungCsvAdresse(monat))}>
            CSV herunterladen
          </Knopf>
        }
      />

      <div className="flex flex-col gap-6 px-6 pb-8">
        <div className="flex flex-wrap items-end gap-6">
          <Select
            size="sm"
            label="Monat"
            items={monatsauswahl()}
            selectedKey={monat}
            onSelectionChange={(schluessel) => {
              if (typeof schluessel === 'string') setParams({ monat: schluessel })
            }}
            className="w-[12rem]"
          >
            {(eintrag) => <Select.Item {...eintrag} />}
          </Select>
          {zustand.status === 'da' ? (
            <div>
              <p className="text-[11.5px] text-quaternary">Gesamt über alle Partner</p>
              <p className="font-display text-2xl leading-[1.15] font-bold text-primary tabular-nums">
                {euro(summe)}
              </p>
            </div>
          ) : null}
        </div>

        {zustand.status === 'laedt' ? (
          <Laedt>Abrechnung wird berechnet …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : zustand.abrechnungen.length === 0 ? (
          <Leer titel="Keine Partner">Ohne Partner gibt es nichts abzurechnen.</Leer>
        ) : (
          zustand.abrechnungen.map((a) => (
            <PartnerAbrechnung key={a.partnerId} a={a} editionsnamen={editionsnamen} />
          ))
        )}
      </div>
    </div>
  )
}

function PartnerAbrechnung({
  a,
  editionsnamen,
}: {
  a: AbrechnungDaten
  editionsnamen: ReadonlyMap<string, string>
}) {
  const ohneAbruf = a.mengen.abrufTage === 0
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[15px] font-semibold text-primary">
          <Link to={`/partner/${a.partnerId}`} className="hover:underline">
            {a.partnerName}
          </Link>
        </h2>
        <span className="text-[12px] text-quaternary tabular-nums">{a.billingRef}</span>
        <span className="text-[12px] text-tertiary tabular-nums">
          {ohneAbruf
            ? 'kein Abruf in diesem Monat'
            : `${a.mengen.abrufTage} Abruftage · ${a.mengen.kundenPeak} Kunden (Peak)`}
        </span>
        <span
          className={cx(
            'ml-auto font-display text-lg font-bold tabular-nums',
            a.ohnePreis.length === 0 ? 'text-primary' : 'text-achtung-tief',
          )}
        >
          {a.ohnePreis.length === 0
            ? euro(a.gesamtCent)
            : `${euro(a.gesamtCent)} · ohne Preis: ${a.ohnePreis.join(', ')}`}
        </span>
      </div>
      <Tabelle kopf={['Artikel', 'Menge', 'Einzelpreis', 'Gesamt']}>
        {a.positionen.map((p) => (
          <tr
            key={[p.artikel, p.edition, p.laufzeit].filter(Boolean).join(':')}
            className="border-b border-secondary last:border-b-0"
          >
            <td className="py-2 pr-2.5 text-primary">{p.bezeichnung}</td>
            <td className="py-2 pr-2.5 text-tertiary tabular-nums">{p.menge}</td>
            <td className="py-2 pr-2.5 text-tertiary tabular-nums">
              {a.ohnePreis.includes(p.edition ?? '') ? '—' : euro(p.einzelpreisCent)}
            </td>
            <td className="py-2 text-primary tabular-nums">
              {a.ohnePreis.includes(p.edition ?? '') ? '—' : euro(p.gesamtCent)}
            </td>
          </tr>
        ))}
      </Tabelle>
      {a.mengen.jeKunde.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-[12.5px] text-tertiary hover:text-primary">
            Zusammensetzung je Kunde ({a.mengen.jeKunde.length})
          </summary>
          <div className="mt-3">
            <Tabelle
              kopf={['Kunde', 'Menge', 'Edition', 'Lizenz', 'Erstellt', 'Letzte Änderung', '']}
            >
              {a.mengen.jeKunde.map((k) => (
                <tr
                  key={k.ref}
                  className={cx(
                    'border-b border-secondary last:border-b-0',
                    k.istDemo && 'text-quaternary',
                  )}
                >
                  <td className="py-2 pr-2.5">
                    <span className={cx('block', !k.istDemo && 'text-primary')}>{k.name}</span>
                    <span className="block text-[11.5px] text-quaternary">{k.ref}</span>
                  </td>
                  <td className="py-2 pr-2.5 tabular-nums">
                    {k.menge}
                    {k.menge !== k.seedsPeak ? (
                      <span className="ml-1 text-[11px] text-quaternary">(Peak {k.seedsPeak})</span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-2.5">{editionsnamen.get(k.edition) ?? k.edition}</td>
                  <td className="py-2 pr-2.5 text-[12.5px] text-tertiary tabular-nums">
                    {k.lizenz
                      ? `${k.lizenz.laufzeit === 'monatlich' ? 'monatlich' : `${k.lizenz.mindestlaufzeitMonate} Monate`} · mind. ${k.lizenz.mindestmenge} · bis ${datum(k.lizenz.laufzeitEnde)}`
                      : '—'}
                  </td>
                  <td className="py-2 pr-2.5 text-tertiary tabular-nums">
                    {k.aktivSeit ? datum(k.aktivSeit) : '—'}
                  </td>
                  <td className="py-2 pr-2.5 text-tertiary tabular-nums">
                    {k.letzteAenderung ? datum(k.letzteAenderung) : '—'}
                  </td>
                  <td className="py-2 text-right">
                    {k.istDemo ? <Status ton="ruhig">Demo, zählt nicht</Status> : null}
                  </td>
                </tr>
              ))}
            </Tabelle>
          </div>
        </details>
      ) : null}
    </section>
  )
}
